import {
  DRIVEN_ROW, drivenLinkReach, unreviewedCount,
  versionAxisOwner, type DrivenVersionFields,
} from '@agavi/blocks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnswerRefScope } from './answer-ref-context';
import { REFUSAL_REASON, useDriverRows } from './use-driver-rows';

import type { BlockIssue } from './block-issues';
import type { Block } from '@/types/experience';

/**
 * WHAT IS WRONG WITH A DRIVEN VERSION AXIS — for every block, from one place.
 *
 * Mounted by `IssuesPanel` beside `useBrokenRefIssues` and for the same reason it gives: the
 * panel reads the block's identity from the context it already sits in, so a cross-cutting
 * check needs no prop and no block file changes. Twelve copies of these four checks would be
 * twelve chances to phrase one of them differently, and the link control already learned that
 * lesson once.
 *
 * ## Why the issue list and not just the control
 *
 * The link trigger already shows the lost state, and a teacher who never opens that menu never
 * sees it. An error that only appears inside the control you would have to suspect is an error
 * nobody finds — which is exactly the gap `branchSetsToCheck` was written to close for versions
 * one axis over.
 */
export function useDrivenLinkIssues(blockId: string | undefined): BlockIssue[] {
  const scope = useAnswerRefScope();
  const { t } = useTranslation();

  const here = scope?.tasks.find(tk => tk.id === scope.currentTaskId);
  const block = blockId ? here?.blocks?.find((b: Block) => b.id === blockId) : undefined;
  const data = block?.data as
    { versions?: ({ id: string } & DrivenVersionFields)[]; drivenBy?: string } | undefined;
  const drivenBy = data?.drivenBy;
  const versions = data?.versions;

  /* ONE derivation of the axis's rows — see `useDriverRows`, which also carries the facet so
     a FITB driver is judged on the BLANK the link names rather than on the block as a whole. */
  const { address, rows, driver, alias, faults } = useDriverRows(drivenBy);
  const partitions = rows !== null;
  /* An alias address that resolves to no alias is the same failure as a block address naming
     no block, and reads as one below. */
  const target = address?.kind === 'alias' ? alias : driver;
  /* A STRING, because `faults` is a fresh array every render and the memo would never hold.
     Its content is exactly what the messages below read, so the two cannot drift. */
  const faultKey = faults.map(f =>
    `${f.kind}:${f.member.blockId}:${f.kind === 'refused' ? f.refusal : f.kind === 'taskScoped' ? f.variable : ''}`)
    .join('\u0000');
  /*
   * WHAT TO CALL THE OFFENDING MEMBER — and the hard case is the one that matters most.
   *
   * A member is almost never in THIS task, so the name is looked up across the experience.
   * But the commonest fault is `missing`, which means the block is DELETED and has no name
   * left to look up — and that is precisely when a teacher most needs to be told where to
   * look. Measured: the message read "A linked block is gone", which names nothing.
   *
   * So it falls back to the member's own stored label, then to the TASK, which survives the
   * block and still says where to go. The bare word is last and should be unreachable.
   */
  const memberName = (member: { taskId: string; blockId: string; label?: string }) => {
    const task = (scope?.tasks ?? []).find((tk: { id: string }) => tk.id === member.taskId);
    const block = (task as { blocks?: Block[] } | undefined)?.blocks
      ?.find((b: Block) => b.id === member.blockId);
    return block?.name
      || member.label
      || (task as { title?: string } | undefined)?.title
      || t('blocks.drivenVersions.aliasMemberFallback');
  };
  /*
   * VERSIONS WHOSE ROW IS GONE — an option the teacher deleted on the driver.
   *
   * `syncDrivenVersions` KEEPS such a version on purpose and hands it back in `orphaned`, so
   * the authored question survives in case the option comes back. But
   * `useDrivenVersionProjection` only reads `versions` from that return, so the `orphaned`
   * half went nowhere: the content was preserved and completely invisible — no warning when
   * the option was deleted, and nothing afterwards.
   *
   * Recomputed here from the rows rather than plumbed through the projection. The projection
   * is an effect that fires on a change; this is a question about the state as it stands, and
   * a teacher opening a block an hour later must get the same answer as one who watched it
   * happen.
   */
  const stranded = rows
    ? (versions ?? []).filter(v => v.drivenRowId && !rows.some(r => r.id === v.drivenRowId)).length
    : 0;
  const owner = data ? versionAxisOwner(data) : 'none';
  /* The same question the link's own mark asks, from the same function — two derivations of
     "can a student get there" is how the control and the issue list come to disagree. */
  const reach = scope && drivenBy
    ? drivenLinkReach(drivenBy, scope.currentTaskId,
      scope.tasks as Parameters<typeof drivenLinkReach>[2], scope.aliases)
    : 'always';
  /*
   * AND THE COPIES NOBODY HAS DECIDED — an ABSENCE, so it waits until the teacher moves on.
   *
   * Out here with `stranded` and for the same reason: it is derived from `rows`, which is a
   * fresh array every render, so counting it inside the memo would either re-run the memo
   * every render or lie about its dependencies.
   *
   * THROUGH `unreviewedCount`, which the `nobody counts unreviewed rows by hand` invariant
   * insists on and was right to: twenty-two hand-rolled filters is how the box and the issue
   * list came to disagree about whether the work was finished. Its name says scenario and its
   * question is "how many of these are copies", which is this one. The two FILTERS are a
   * different question — which rows are in scope — so they stay here.
   *
   * A version stops counting the moment it is edited, which every block's writer does through
   * `markVersionReviewed`.
   *
   * BLANK is excluded on purpose. It is the row a teacher is LEAST likely to want different
   * from the others — a student who skipped the driver gets the question as written — so
   * demanding a decision there would make the count cry wolf on nearly every linked block.
   *
   * EXCEPT WHERE THE DRIVER IS ONLY SOMETIMES REACHED, and then that reasoning inverts. The
   * exclusion assumes skipping is an edge case. On a `sometimes` link it is not an edge case,
   * it is a whole route: every student who took the other path lands on Blank, and an
   * undecided Blank is version one's question asked of people who never saw the driver. That
   * is the one shape where a conditional link is genuinely broken, and it is also what makes
   * the link SAFE to offer at all — the row is the repair, so the gate is whether the teacher
   * has made it. `always` keeps the old behaviour exactly; `never` is already an error above
   * and does not need a second sentence about Blank.
   *
   * A STRANDED VERSION is excluded too, which used to be handled a layer down and invisibly:
   * `syncDrivenVersions` cleared `unreviewed` as it parked one. It no longer does, because
   * that mark is the only record of "nobody has touched this" and the projection reads it to
   * decide whether a bank is safe to discard on a re-point. The question here was always "how
   * many versions a student can REACH are undecided", so it now asks that.
   */
  const pending = unreviewedCount((versions ?? []).filter(
    v => ((reach === 'sometimes') || v.drivenRowId !== DRIVEN_ROW.blank)
      && (!rows || rows.some(r => r.id === v.drivenRowId))));

  return useMemo(() => {
    const category = t('blocks.drivenVersions.issueCategory');
    const issues: BlockIssue[] = [];

    /*
     * A CONTRADICTION, not an absence, so it surfaces at once: the block holds authored
     * questions and has nothing that can choose between them. Three ways in, and they are
     * genuinely different repairs, so they are three messages rather than one.
     */
    if (owner === 'orphaned') {
      // The link was dropped by a paste that could not resolve the address. Content kept.
      issues.push({
        key: 'driven-link-lost', severity: 'error', kind: 'defect', category,
        detail: t('blocks.drivenVersions.issueLost'),
      });
    } else if (drivenBy && !target) {
      // Still addressed, but the block it names is gone or was never in this experience.
      issues.push({
        key: 'driven-driver-missing', severity: 'error', kind: 'defect', category,
        detail: t('blocks.drivenVersions.issueMissing'),
      });
    } else if (drivenBy && target && reach === 'never') {
      /*
       * The driver exists and partitions fine, and no route from here ever passes through it —
       * so the axis can only ever resolve to Blank and every other version is dead content.
       *
       * `sometimes` says nothing, and the claim under that silence is now TRUE: the Blank row
       * does catch the routes that miss the driver. It briefly did not, and this briefly
       * carried a warning saying so — `drivenVersionRoll` had an `attempted` veto that gave
       * those students version one instead. The veto is gone, so the warning is too, and the
       * gate that remains is the right one: `pending` below counts an undecided Blank on a
       * `sometimes` link, because there it is a whole route rather than an edge case.
       */
      issues.push({
        key: 'driven-driver-unreachable', severity: 'error', kind: 'defect', category,
        detail: t('blocks.drivenVersions.issueUnreachable'),
      });
    } else if (faults.length) {
      /*
       * AN ALIAS WHOSE MEMBER CANNOT DRIVE — and it says WHICH member and why.
       *
       * Not folded into `issueUnusable`, which says "no longer has options to link to" and
       * would be a lie here: the alias is fine and ONE of several blocks is not. A teacher
       * told that has nowhere to look, and an alias with four members is four places to look
       * in. Each fault names its block, so each is its own line.
       *
       * The `taskScoped` one carries the repair the owner wrote, and the repair is promotion
       * rather than renaming: appending the task name to make `{site}` unique is the SPR
       * long-name trap and was rejected.
       */
      for (const fault of faults) {
        const name = memberName(fault.member);
        issues.push({
          key: `driven-alias-${fault.kind}-${fault.member.blockId}`,
          severity: 'error', kind: 'defect', category,
          detail: fault.kind === 'missing'
            /* A LOCATION, not a name: `missing` means the lookup FAILED by definition, so
               "{{block}} is gone" could only ever print a fallback. The task survives the
               block and is where the teacher has to go. */
            ? t('blocks.drivenVersions.issueAliasMissing', { where: name })
            : fault.kind === 'taskScoped'
              ? t('blocks.drivenVersions.issueAliasTaskScoped',
                { block: name, variable: fault.variable })
              : t('blocks.drivenVersions.issueAliasRefused',
                { block: name, reason: t(REFUSAL_REASON[fault.refusal]) }),
        });
      }
    } else if (drivenBy && !partitions) {
      /*
       * The driver is there and no longer has an answer space to partition — switched to
       * multi-select, or had its last option deleted. Every version but Blank is then dead,
       * which is a worse failure than a missing block and reads nothing like one.
       */
      issues.push({
        key: 'driven-driver-unusable', severity: 'error', kind: 'defect', category,
        detail: t('blocks.drivenVersions.issueUnusable'),
      });
    }

    /*
     * A VERSION WHOSE ROW IS GONE. Reported rather than removed: the question is authored
     * work, it is kept in case the option comes back, and the teacher is the only one who can
     * say whether it still has a job. A warning and not an error — nothing is wrong with the
     * block as it stands, there is simply content no student can now be shown.
     *
     * `incompletion`, so it waits until the block is settled like the copy count does: an
     * option deleted mid-edit would otherwise nag while the teacher is still mid-thought.
     */
    if (stranded > 0) {
      issues.push({
        key: 'driven-version-stranded', severity: 'warning', kind: 'incompletion', category,
        detail: t('blocks.drivenVersions.issueStranded', { count: stranded }),
      });
    }

    if (pending > 0) {
      issues.push({
        key: 'driven-versions-unreviewed', severity: 'warning', kind: 'incompletion', category,
        detail: t('blocks.drivenVersions.issueUnreviewed', { count: pending }),
      });
    }
    return issues;
    // `pending` and `stranded` are the row-derived scalars, computed above for exactly this
    // reason: `rows` and `versions` are fresh identities every render, and the two counts are
    // the only thing about them this memo depends on. `faultKey` does the same job for
    // `faults`, and `scope?.tasks` is what `memberName` reads — both are the stable halves of
    // identities that are otherwise rebuilt on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, drivenBy, target, partitions, reach, stranded, pending, faultKey, scope?.tasks, t]);
}
